import { ApiResponse, Company } from "../types";
import { apiClient } from "./apiClient";

export interface LoginCredentials {
  email: string;
  password: string;
}

export interface LoginResponse {
  data: {
    token: string;
    user: {
      id: number;
      email: string;
      name: string;
      userType: string;
      companyId: number;
    };
    company: Company;
  };
  message: string;
  success: boolean;
}

export interface ResetPasswordData {
  currentPassword: string;
  newPassword: string;
}

export interface AuthService {
  login: (credentials: LoginCredentials) => Promise<ApiResponse<LoginResponse>>;
  logout: () => void;
  getToken: () => string | null;
  isAuthenticated: () => boolean;
  getUserData: () => LoginResponse["data"]["user"] | null;
  getCompanyData: () => Company | null;
  updateCompanyData: (companyData: Partial<Company>) => void;
  resetPassword: (data: ResetPasswordData) => Promise<ApiResponse<{ message: string }>>;
}

class AuthServiceImpl implements AuthService {
  private readonly tokenKey = "auth_token";
  private readonly userDataKey = "user_data";
  private readonly companyDataKey = "company_data";

  async login(
    credentials: LoginCredentials
  ): Promise<ApiResponse<LoginResponse>> {
    try {
      // Use apiClient but without auth since we're logging in
      const response = await apiClient.post<LoginResponse>(
        "/auth/login/company",
        credentials,
        { requiresAuth: false }
      );

      // Extract token and user data from nested response
      const token = response.data.token;
      const user = response.data.user;
      const company = response.data.company;

      // Store auth and company data
      localStorage.setItem(this.tokenKey, token);
      localStorage.setItem(this.userDataKey, JSON.stringify(user));
      localStorage.setItem(this.companyDataKey, JSON.stringify(company));

      return {
        success: true,
        data: response,
      };
    } catch (error: any) {
      console.error("Login error:", error);
      return {
        success: false,
        error: error.message || "Network error. Please try again.",
      };
    }
  }

  logout(): void {
    localStorage.removeItem(this.tokenKey);
    localStorage.removeItem(this.userDataKey);
    localStorage.removeItem(this.companyDataKey);
    window.location.href = "/login";
  }

  getToken(): string | null {
    const token = localStorage.getItem(this.tokenKey);
    // Old tokens included base64 logo/signature and trigger HTTP 431.
    if (token && token.length > 12000) {
      localStorage.removeItem(this.tokenKey);
      localStorage.removeItem(this.userDataKey);
      localStorage.removeItem(this.companyDataKey);
      return null;
    }
    return token;
  }

  isAuthenticated(): boolean {
    const token = this.getToken();
    return token !== null;
  }

  getUserData(): LoginResponse["data"]["user"] | null {
    const userData = localStorage.getItem(this.userDataKey);
    if (!userData) return null;

    try {
      return JSON.parse(userData);
    } catch {
      return null;
    }
  }

  getCompanyData(): Company | null {
    const companyData = localStorage.getItem(this.companyDataKey);
    if (!companyData) return null;

    try {
      return JSON.parse(companyData);
    } catch {
      return null;
    }
  }

  updateCompanyData(updatedCompanyData: Partial<Company>): void {
    const currentCompanyData = this.getCompanyData();
    if (currentCompanyData) {
      const mergedCompanyData = {
        ...currentCompanyData,
        ...updatedCompanyData,
      };
      localStorage.setItem(
        this.companyDataKey,
        JSON.stringify(mergedCompanyData)
      );
    }
  }

  async resetPassword(data: ResetPasswordData): Promise<ApiResponse<{ message: string }>> {
    try {
      const response = await apiClient.post<{ message: string }>(
        "/auth/reset-password",
        data
      );

      return {
        success: true,
        data: response,
      };
    } catch (error: any) {
      console.error("Reset password error:", error);
      return {
        success: false,
        error: error.message || "Failed to reset password",
      };
    }
  }
}

// Export singleton instance
export const authService: AuthService = new AuthServiceImpl();
